<script setup lang="ts" xmlns:template="http://www.w3.org/1999/html">
  import TopBar from '@/components/TopBar.vue'
  import { useShipmentStore } from '@/store/shipment.store'
  import type { Shipment } from '@/types/nest/shipment'
  import { axiosClient } from '@/utils/axios-client'
  import { createBaseColumns } from '@/utils/table'
  import type { Nullish } from '@logistiek-zonder-papier/lzp-library'
  import {
    DateRangePicker,
    Table,
    UiButton,
    useToastStore,
  } from '@logistiek-zonder-papier/lzp-library'
  import { isAxiosError } from 'axios'
  import { isAfter, isBefore, startOfToday, subDays } from 'date-fns'
  import { computed, onMounted, ref } from 'vue'

  const shipmentStore = useShipmentStore()

  const errorMessage = ref<string | null>()
  const fromDate = ref<Date | Nullish>(subDays(startOfToday(), 7))
  const toDate = ref<Date | Nullish>(startOfToday())
  const toastStore = useToastStore()

  const shipments = computed<Shipment[]>(
    () =>
      shipmentStore.shipments
        .filter(shipment => {
          if (shipment.startTime == null) return false
          const startTime = new Date(shipment.startTime)
          if (isBefore(startTime, toDate.value ?? '') && isAfter(startTime, fromDate.value ?? '')) {
            return true
          }
          if (shipment.endTime == null) return false

          const endTime = new Date(shipment.endTime)
          return isBefore(endTime, toDate.value ?? '') && isAfter(endTime, fromDate.value ?? '')
        })
        .sort(
          (lhs, rhs) =>
            new Date(rhs.startTime ?? '').getTime() - new Date(lhs.startTime ?? '').getTime()
        ) //TODO move this to table sort function
  )

  onMounted(async () => {
    await fetchShipments()
  })

  async function fetchShipments() {
    try {
      await shipmentStore.fetchShipments()
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        errorMessage.value = error.response.data.message
      } else {
        throw error
      }
    }
  }

  async function excelExport() {
    if (shipments.value.length === 0) {
      toastStore.error('Geen zendingen gevonden in deze datum range')
      return
    }
    const response = await axiosClient.request({
      url: '/excel/bgb', // TODO support other export types
      responseType: 'blob',
      params: {
        startTime: fromDate.value?.toISOString(),
        endTime: toDate.value?.toISOString(),
      },
    })

    const disposition = response.headers['content-disposition']
    let filename = 'rapport.xlsx'
    if (disposition) {
      const match = disposition.match(/filename="?([^"]+)"?/)
      if (match) filename = match[1]
    }

    const blob = new Blob([response.data], {
      type: String(response.headers['content-type']),
    })
    const blobUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = filename
    link.click()
    URL.revokeObjectURL(blobUrl)
  }

  async function bulkPdf() {
    if (shipments.value.length === 0) {
      toastStore.error('Geen zendingen gevonden in deze datum range')
      return
    }
    const response = await axiosClient.request({
      url: `/shipments/pdf-bulk`,
      method: 'POST',
      responseType: 'blob',
      data: {
        shipmentIds: shipments.value.map(shipment => shipment.id),
      },
    })

    window.open(URL.createObjectURL(response.data), '_blank')
  }

  async function openPdf(row: Shipment) {
    const response = await axiosClient.request({
      url: `/shipments/${row.id}/pdf`,
      method: 'POST',
      responseType: 'blob',
    })

    window.open(URL.createObjectURL(response.data), '_blank')
  }

  const columns = computed(() => {
    const rows = shipmentStore.shipments
    const baseColumns = createBaseColumns(rows, [
      /createdAt/,
      /updatedAt/,
      /deletedAt/,
      /id/,
      /Id$/,
      /customer.bi/,
      /customer.auth/,
      /customer.iban/,
      /customer.taxNumber/,
    ])
    baseColumns.push({ key: 'action', label: 'Acties', sticky: 'right' })
    return baseColumns
  })
</script>

<template>
  <TopBar />
  <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
  <div class="layout" v-else>
    <DateRangePicker
      :fromOptions="{ label: 'Van' }"
      :toOptions="{ label: 'Tot' }"
      v-model:from="fromDate"
      v-model:to="toDate"
    />
    <Table
      :rows="shipments"
      :columns="columns"
      :defaultColumns="[
        'startTime',
        'endTime',
        'fromAddress.street',
        'fromAddress.city',
        'toAddress.street',
        'toAddress.city',
        'truck.licensePlate',
        'action',
      ]"
      :searchable="true"
      configurationStorageKey="shipmentOverview"
      :configurable="true"
    >
      <template #table-top-actions>
        <UiButton label="Bulk PDF" variant="prominent" @click="bulkPdf" />
        <UiButton label="Excel Export" variant="prominent" @click="excelExport" />
      </template>
      <template #cell-action="{ row }">
        <UiButton size="small" label="PDF" variant="prominent" @click="openPdf(row)" />
      </template>
      <template #cell-truck="{ row }">{{ row.truck?.licensePlate ?? 'N/A' }}</template>
      <template #cell-fromAddress="{ row }">{{ row.fromAddress.street2 }}</template>
      <template #cell-startTime="{ row }">{{
        row.startTime ? new Date(row.startTime).toLocaleString() : '-'
      }}</template>
      <template #cell-endTime="{ row }">{{
        row.endTime ? new Date(row.endTime).toLocaleString() : '-'
      }}</template>
      <template #cell-toAddress="{ row }">{{ row.toAddress.street2 }}</template>
      <template #cell-number="{ row }">{{
        row.bgb?.groupId ?? row.ecmr?.domainIdentifier ?? 'N/A'
      }}</template>
    </Table>
  </div>
</template>

<style lang="css" scoped>
  .error-message {
    color: var(--colours-support-negative);
  }

  .create-button {
    align-self: end;
  }

  .button-group {
    display: flex;
    flex-direction: row;
    gap: var(--spacing-xs);
  }

  .layout {
    display: flex;
    padding: var(--spacing-m);
    flex-direction: column;
    gap: var(--spacing-s);
    height: 90vh;
  }
</style>
